/**
 * Font Library API client (/api/tend/fonts).
 *
 * Font families are Google Fonts copies or custom uploads, self-hosted on S3.
 * Scoping: universal (shared with every brand, SuperAdmin-managed) or brand
 * (private, editor+ manageable). Custom uploads require a license attestation.
 *
 * The manifest endpoint is the single contract consumed by editors and
 * renderers — see utils/fontResolution.ts for the cascade rules.
 */
import axios from 'axios';
import apiClient from './apiClient';
import { formatAuthHeader, getStoredToken, isTokenExpired } from '../utils/authUtils';
import type { FontManifest } from '../utils/fontResolution';
import setupTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';

export type { FontManifest };

// ========== Types ==========

export type FontScope = 'universal' | 'brand';
export type FontStyle = 'normal' | 'italic';
export type FontElement = 'title' | 'subheader' | 'body' | 'category';

export interface FontVariant {
  id: number;
  family_id: number;
  weight: number;
  style: FontStyle;
  format: string;
  s3_key: string;
  s3_url: string;
  file_size_bytes: number | null;
  original_filename: string | null;
}

export interface FontFamily {
  id: number;
  brand_id: number | null;
  is_universal: boolean;
  name: string;
  slug: string;
  source: 'google' | 'custom';
  google_family: string | null;
  category: string | null;
  is_active: boolean;
  attested_by_user_id: number | null;
  attested_at: string | null;
  created_by: number | null;
  created_at: string | null;
  variants: FontVariant[];
}

export interface FontListResponse {
  families: FontFamily[];
  google_enabled: boolean;
  attestation_text: string;
}

export interface GoogleCatalogItem {
  family: string;
  category: string;
  variants: string[];
  added_universal: boolean;
  added_brand: boolean;
}

export interface GoogleCatalogResponse {
  total: number;
  items: GoogleCatalogItem[];
}

export interface FontDefaultSetting {
  font_family_id: number;
}

export type FontDefaultsMap = Record<FontElement, FontDefaultSetting | null>;

export interface ResolvedFontDefault {
  font_family_id: number;
  source: 'brand' | 'universal';
  family_name: string | null;
  family_slug: string | null;
}

export interface BrandFontDefaultsResponse {
  brand: FontDefaultsMap;
  resolved: Record<FontElement, ResolvedFontDefault | null>;
}

// Attestation copy — must match ATTESTATION_TEXT in
// backend/api_gateway/models/fonts.py (the backend stores its own verbatim).
export const FONT_ATTESTATION_TEXT =
  "I confirm that I own this font or hold a license that permits its use in " +
  "Tend's content creation and rendering, including hosting, embedding, and " +
  "export into images and video. I accept responsibility for its use as " +
  "described in the Terms of Service.";

// Multipart client (apiClient forces JSON content-type)
const fontUploadClient = axios.create({ baseURL: '/api', timeout: 60000 });
// Shared refresh interceptor (attach-current-token + 401 heal) — the old
// local interceptor dropped the header when the token was expired.
setupTokenRefreshInterceptor(fontUploadClient);

// ========== API ==========

export const fontsApi = {
  /** Universal + (optionally) one brand's families. */
  async list(brandSlug?: string, includeInactive = false): Promise<FontListResponse> {
    return apiClient.get<FontListResponse>('tend/fonts', {
      ...(brandSlug ? { brand_slug: brandSlug } : {}),
      ...(includeInactive ? { include_inactive: true } : {}),
    });
  },

  /** Search the Google Fonts catalog (popularity order). */
  async googleCatalog(params: {
    search?: string;
    category?: string;
    limit?: number;
    offset?: number;
    brandSlug?: string;
  }): Promise<GoogleCatalogResponse> {
    return apiClient.get<GoogleCatalogResponse>('tend/fonts/google/catalog', {
      ...(params.search ? { search: params.search } : {}),
      ...(params.category ? { category: params.category } : {}),
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
      ...(params.brandSlug ? { brand_slug: params.brandSlug } : {}),
    });
  },

  /** Copy a Google family into the library (files re-hosted on S3). */
  async addGoogleFont(body: {
    family: string;
    scope: FontScope;
    brand_slug?: string;
    variants?: string[];
  }): Promise<FontFamily> {
    return apiClient.post<FontFamily>('tend/fonts/google/add', body);
  },

  /** Create an empty custom family; requires attestation_accepted=true. */
  async createCustomFamily(body: {
    name: string;
    scope: FontScope;
    brand_slug?: string;
    attestation_accepted: boolean;
  }): Promise<FontFamily> {
    return apiClient.post<FontFamily>('tend/fonts/custom', body);
  },

  /** Upload one variant file (ttf/otf/woff/woff2) to a family. */
  async uploadVariant(
    familyId: number,
    file: File,
    weight: number,
    style: FontStyle,
    replace = false,
  ): Promise<FontFamily> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('weight', String(weight));
    formData.append('style', style);
    formData.append('replace', String(replace));
    const response = await fontUploadClient.post<FontFamily>(
      `/tend/fonts/${familyId}/variants`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  /** Soft delete — existing renders keep working; font can't be newly picked. */
  async deactivate(familyId: number): Promise<{ deactivated: boolean; id: number }> {
    return apiClient.delete(`tend/fonts/${familyId}`);
  },

  async restore(familyId: number): Promise<FontFamily> {
    return apiClient.post<FontFamily>(`tend/fonts/${familyId}/restore`, {});
  },

  /** Hard delete of a single variant (fix bad uploads). */
  async deleteVariant(familyId: number, variantId: number): Promise<{ deleted: boolean; id: number }> {
    return apiClient.delete(`tend/fonts/${familyId}/variants/${variantId}`);
  },

  // ----- Defaults (universal/brand cascade layers) -----

  async getUniversalDefaults(): Promise<{ defaults: FontDefaultsMap }> {
    return apiClient.get('tend/fonts/defaults/universal');
  },

  /** SuperAdmin only; value null clears an element's default. */
  async putUniversalDefaults(
    defaults: Partial<FontDefaultsMap>,
  ): Promise<{ defaults: FontDefaultsMap }> {
    return apiClient.put('tend/fonts/defaults/universal', { defaults });
  },

  async getBrandDefaults(brandSlug: string): Promise<BrandFontDefaultsResponse> {
    return apiClient.get(`tend/fonts/defaults/${brandSlug}`);
  },

  /** Editor+ on the brand; value null clears (falls back to universal). */
  async putBrandDefaults(
    brandSlug: string,
    defaults: Partial<FontDefaultsMap>,
  ): Promise<{ brand: FontDefaultsMap }> {
    return apiClient.put(`tend/fonts/defaults/${brandSlug}`, { defaults });
  },

  // ----- Manifest (single contract for editors + renderers) -----

  async getManifest(brandSlug: string): Promise<FontManifest> {
    return apiClient.get<FontManifest>(`tend/fonts/manifest/${brandSlug}`);
  },
};

export default fontsApi;
